// MenuRankingPanel — Phase 5a menus endpoint.

import { useMemo, useState } from 'react';
import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  HeatCell,
  LoadingState,
  SectionHead,
  ShareBar,
  SortCaret,
  Sparkline,
} from '../bits';
import { useMenus, type AnalyticsScope } from '../queries';
import {
  fmtNum,
  fmtWonShort,
  platformColor,
  platformShort,
} from '../../../lib/format';
import type { AnalyticsMenuRow } from '../../../types/api';

interface Props {
  scope: AnalyticsScope;
  limit?: number;
}

type SortKey = 'name' | 'revenue' | 'sold' | 'cost' | 'margin';

export function MenuRankingPanel({ scope, limit }: Props) {
  const q = useMenus(scope);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'revenue',
    dir: 'desc',
  });
  // Phase 5b-3 — 메뉴 row 클릭 시 단순 highlight (시안에 menu detail view 없음).
  const [selectedMenuKey, setSelectedMenuKey] = useState<string | null>(null);

  const sorted = useMemo<AnalyticsMenuRow[]>(() => {
    if (!q.data) return [];
    const cp = [...q.data];
    cp.sort((a, b) => {
      const av = a[sort.key as keyof AnalyticsMenuRow];
      const bv = b[sort.key as keyof AnalyticsMenuRow];
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''));
      return cmp * (sort.dir === 'asc' ? 1 : -1);
    });
    return limit ? cp.slice(0, limit) : cp;
  }, [q.data, sort, limit]);

  const maxRev = useMemo(() => {
    if (!sorted.length) return 1;
    return Math.max(...sorted.map((m) => m.revenue));
  }, [sorted]);

  const Th = ({
    keyId,
    label,
    align,
  }: {
    keyId: SortKey;
    label: string;
    align?: 'left' | 'right';
  }) => (
    <div
      onClick={() =>
        setSort((s) => ({
          key: keyId,
          dir: s.key === keyId && s.dir === 'desc' ? 'asc' : 'desc',
        }))
      }
      style={{
        cursor: 'pointer',
        textAlign: align ?? 'left',
        userSelect: 'none',
        padding: '8px 10px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--fg-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
      <SortCaret dir={sort.key === keyId ? sort.dir : null} />
    </div>
  );

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead
        title="메뉴 순위"
        sub={
          q.data
            ? `${q.data.length}개 항목${
                limit && q.data.length > limit ? ` (상위 ${limit})` : ''
              }`
            : undefined
        }
      />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && q.data.length === 0 && <EmptyState />}
      {sorted.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 720 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  '24px 1.6fr 70px 1.1fr 0.7fr 0.8fr 0.8fr 1fr',
                alignItems: 'center',
              }}
            >
              <div />
              <Th keyId="name" label="메뉴" />
              <div
                style={{
                  padding: '8px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--fg-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                플랫폼
              </div>
              <Th keyId="revenue" label="매출" align="right" />
              <Th keyId="sold" label="판매" align="right" />
              <Th keyId="cost" label="원가율" align="right" />
              <Th keyId="margin" label="마진" align="right" />
              <div
                style={{
                  padding: '8px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--fg-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                추이
              </div>
            </div>
            <div
              style={{
                height: 1,
                background: 'var(--border-weak)',
              }}
            />
            {sorted.map((m, i) => {
              const rowKey =
                m.bucketKey ?? m.menuId ?? `${m.name}-${m.platformId}-${i}`;
              const active = selectedMenuKey === rowKey;
              return (
              <div
                key={rowKey}
                onClick={() =>
                  setSelectedMenuKey((cur) => (cur === rowKey ? null : rowKey))
                }
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    '24px 1.6fr 70px 1.1fr 0.7fr 0.8fr 0.8fr 1fr',
                  alignItems: 'center',
                  padding: '6px 0',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: active ? 'var(--primary-bg)' : 'transparent',
                  border: active
                    ? '1px solid var(--primary-border)'
                    : '1px solid transparent',
                  transition: 'background 120ms',
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    color:
                      i < 3
                        ? 'var(--primary-strong)'
                        : 'var(--fg-3)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {i + 1}
                </div>
                <div
                  style={{
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--fg-1)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.name}
                  </span>
                  {(m.categoryName ?? m.category) && (
                    <span
                      style={{
                        fontSize: 10,
                        color: 'var(--fg-3)',
                        padding: '1px 6px',
                        background: 'var(--bg-muted)',
                        borderRadius: 3,
                        flexShrink: 0,
                      }}
                    >
                      {m.categoryName ?? m.category}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 999,
                      background: platformColor(m.platformId),
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                    {platformShort(m.platformId)}
                  </span>
                </div>
                <div
                  style={{
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <ShareBar value={m.revenue} max={maxRev} />
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--fg-1)',
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: 60,
                      textAlign: 'right',
                    }}
                  >
                    {fmtWonShort(m.revenue)}
                  </span>
                </div>
                <div
                  style={{
                    padding: '6px 10px',
                    textAlign: 'right',
                    fontSize: 12,
                    color: 'var(--fg-2)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmtNum(m.sold)}건
                </div>
                <div
                  style={{
                    padding: '6px 10px',
                    textAlign: 'right',
                  }}
                >
                  {m.cost != null ? <HeatCell value={m.cost} /> : '-'}
                </div>
                <div
                  style={{
                    padding: '6px 10px',
                    textAlign: 'right',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--fg-1)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {m.margin != null ? fmtWonShort(m.margin) : '-'}
                </div>
                <div style={{ padding: '6px 10px' }}>
                  {m.trend && m.trend.length > 0 ? (
                    <Sparkline
                      data={m.trend}
                      width={90}
                      height={24}
                      color="var(--primary-strong)"
                    />
                  ) : (
                    <span
                      style={{ fontSize: 11, color: 'var(--fg-4)' }}
                    >
                      -
                    </span>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
